import {Button, Card, Choice, DataStatus, Upload} from 'platform/components';
import {Box, Heading, Right, Text, VStack} from 'platform/foundation';

import {useEffect, useState} from 'react';

import {isNil} from 'ramda';

import {suffixTestId, TestIdProps} from 'shared/web';

import i18n from '../../../i18n/i18n';
import {useImportMarket} from '../hooks/useImportMarket';

const ACCEPTED_FILE_TYPES = '.csv,.xls,.xlsx';

interface ImportUploadFormProps extends TestIdProps {
  onImport: (file: File, market: string) => void;
}

export function ImportUploadForm(props: ImportUploadFormProps) {
  const {marketOptions, defaultMarket, isLoading} = useImportMarket();
  const [market, setMarket] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  useEffect(() => {
    if (!isLoading && isNil(market)) {
      setMarket(defaultMarket);
    }
  }, [isLoading, defaultMarket, market]);

  const effectiveMarket = market ?? defaultMarket;

  const handleSubmit = () => {
    if (isNil(selectedFile)) {
      return;
    }
    props.onImport(selectedFile, effectiveMarket);
  };

  return (
    <Box maxWidth={150} width="100%">
      <Card data-testid={suffixTestId('card', props)}>
        <DataStatus
          isLoading={isLoading}
          data-testid={suffixTestId('languageLoadingStatus', props)}
        >
          <Box padding={6}>
            <VStack spacing={6}>
              <Heading size={2}>{i18n.t('page.import.labels.title')}</Heading>
              <Text size="small" color="secondary">
                {i18n.t('page.import.labels.subtitle')}
              </Text>
              <Choice
                label={i18n.t('page.import.labels.market')}
                value={effectiveMarket}
                options={marketOptions}
                onChange={(value) => setMarket(value ?? defaultMarket)}
                isNotClearable
                menuInPortal
                data-testid={suffixTestId('marketChoice', props)}
              />
              <Upload
                type="card"
                accept={ACCEPTED_FILE_TYPES}
                beforeUpload={(file) => {
                  setSelectedFile(file);
                  return false;
                }}
                data-testid={suffixTestId('fileDropZone', props)}
              />
              <Right>
                <Button
                  title={i18n.t('page.import.actions.importFile')}
                  isDisabled={isNil(selectedFile)}
                  onClick={handleSubmit}
                  data-testid={suffixTestId('importButton', props)}
                />
              </Right>
            </VStack>
          </Box>
        </DataStatus>
      </Card>
    </Box>
  );
}
